import { FormEvent, useState, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { RadioGroup } from '@/components/ui/RadioGroup';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { extractValidationErrors, getErrorMessage } from '@/utils/errorHandler';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrencyFractionDigits } from '@/hooks/useCurrencyFractionDigits';
import { decimalToMinorUnits } from '@/utils/money';
import type {
    Ledger,
    CreateLedgerData,
    UpdateLedgerData,
    LedgerType,
    Collection,
    Tag,
} from '@/api/types';

interface LedgerFormProps {
    type: LedgerType;
    collections: Collection[];
    tags: Tag[];
    ledger?: Ledger;
    onSubmit: (data: CreateLedgerData | UpdateLedgerData) => Promise<void>;
    onCancel: () => void;
    isLoading?: boolean;
    defaultCurrency?: string;
    preselectedCollectionId?: number;
}

/**
 * Form for creating or editing a ledger (asset or debt).
 * - Create mode: Shows initial value, currency, and date fields
 * - Edit mode: Only shows metadata fields (no value editing here)
 */
export function LedgerForm({
    type,
    collections,
    tags,
    ledger,
    onSubmit,
    onCancel,
    isLoading = false,
    defaultCurrency = 'USD',
    preselectedCollectionId,
}: LedgerFormProps) {
    const { config } = useAuth();
    const isEditing = !!ledger;
    const typeLabel = type === 'asset' ? 'Asset' : 'Debt';

    // Get tracking mode values from config, with fallback defaults
    const trackingModeValues = config?.enums?.trackingMode ?? { FINANCIAL: 1, OTHER: 2 };
    const TRACKING_MODE_FINANCIAL = trackingModeValues.FINANCIAL ?? 1;
    const TRACKING_MODE_OTHER = trackingModeValues.OTHER ?? 2;

    // Get validation constraints from config, with fallback defaults
    const labelMaxLength = config?.constraints?.label?.maxLength ?? 255;
    const initialValueMin = config?.constraints?.initialValue?.min ?? 0;

    // Filter collections and tags by type
    const filteredCollections = useMemo(
        () => collections.filter((c) => c.type === type),
        [collections, type]
    );
    const filteredTags = useMemo(
        () => tags.filter((t) => t.type === type),
        [tags, type]
    );

    // Form state
    const [label, setLabel] = useState(ledger?.label || '');
    // Use preselected collection for create, or ledger's collection for edit
    const [collectionId, setCollectionId] = useState<number>(
        ledger?.collectionId || preselectedCollectionId || filteredCollections[0]?.id || 0
    );
    const [tagIds, setTagIds] = useState<number[]>(ledger?.tagIds || []);
    const [description, setDescription] = useState(ledger?.description || '');
    // Tracking mode: FINANCIAL or OTHER (from config)
    const [trackingMode, setTrackingMode] = useState<number>(ledger?.trackingMode ?? TRACKING_MODE_FINANCIAL);

    // Determine effective collection ID (preselected takes priority in create mode)
    const effectiveCollectionId = !isEditing && preselectedCollectionId
        ? preselectedCollectionId
        : collectionId;

    // Initial value fields (only for create)
    const [initialValue, setInitialValue] = useState('');
    const [initialCurrency, setInitialCurrency] = useState(defaultCurrency);
    const [initialDate, setInitialDate] = useState(() => {
        // Default to today in YYYY-MM-DD format
        const today = new Date();
        return today.toISOString().split('T')[0] ?? '';
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [generalError, setGeneralError] = useState<string | null>(null);
    const initialFractionDigits = useCurrencyFractionDigits(initialCurrency);

    const collectionOptions = filteredCollections.map((c) => ({
        value: c.id,
        label: c.label,
    }));

    const tagOptions = filteredTags.map((t) => ({
        value: t.id,
        label: t.label,
    }));

    const trackingModeOptions = [
        {
            value: TRACKING_MODE_FINANCIAL,
            label: 'Financial',
            description: 'Cash movements impact value (portfolios, bank accounts)',
        },
        {
            value: TRACKING_MODE_OTHER,
            label: 'Other',
            description: 'Cash movements tracked separately (real estate, collectibles)',
        },
    ];

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setErrors({});
        setGeneralError(null);

        // Validation
        if (!label.trim()) {
            setErrors({ label: 'Label is required' });
            return;
        }

        // Validate label max length using config constraint
        if (label.length > labelMaxLength) {
            setErrors({ label: `Label must be ${labelMaxLength} characters or less` });
            return;
        }

        if (!effectiveCollectionId) {
            setErrors({ collectionId: 'Collection is required' });
            return;
        }

        let initialValueInMinor: number | undefined;
        if (!isEditing) {
            // Validate initial value fields for create
            if (!initialValue.trim()) {
                setErrors({ initialValue: 'Initial value is required' });
                return;
            }

            try {
                initialValueInMinor = decimalToMinorUnits(initialValue, initialFractionDigits);
                // Scale the configured minimum rather than re-encoding it: a fractional
                // minimum is not expressible in a 0-digit currency like JPY, and the
                // resulting "decimal places" error would hide the real message below.
                const minimumInMinor = Math.round(initialValueMin * 10 ** initialFractionDigits);
                if (initialValueInMinor < minimumInMinor) {
                    throw new Error(`Initial value must be ${initialValueMin} or greater`);
                }
            } catch (error) {
                setErrors({ initialValue: error instanceof Error ? error.message : 'Invalid initial value' });
                return;
            }

            if (!initialCurrency) {
                setErrors({ initialCurrency: 'Currency is required' });
                return;
            }

            if (!initialDate) {
                setErrors({ initialDate: 'Date is required' });
                return;
            }
        }

        try {
            // Debts always use FINANCIAL tracking mode
            const effectiveTrackingMode = type === 'asset' ? trackingMode : TRACKING_MODE_FINANCIAL;

            if (isEditing) {
                const updateData: UpdateLedgerData = {
                    label,
                    collectionId: effectiveCollectionId,
                    tagIds,
                    description: description || null,
                    trackingMode: effectiveTrackingMode,
                };
                await onSubmit(updateData);
            } else {
                const createData: CreateLedgerData = {
                    label,
                    collectionId: effectiveCollectionId,
                    type,
                    initialValue: initialValueInMinor as number,
                    initialCurrency,
                    initialDate: initialDate as string, // Send Y-m-d date string directly
                    tagIds,
                    description: description || null,
                    trackingMode: effectiveTrackingMode,
                };
                await onSubmit(createData);
            }
        } catch (error) {
            const validationErrors = extractValidationErrors(error);

            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
            } else {
                setGeneralError(getErrorMessage(error));
            }
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {generalError && (
                <ErrorAlert
                    message={generalError}
                    onDismiss={() => setGeneralError(null)}
                />
            )}

            <Input
                label="Label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                error={errors.label}
                placeholder={`My ${typeLabel}`}
                required
                maxLength={labelMaxLength}
            />

            {/* Collection selector - only show in edit mode or if no preselected collection */}
            {(isEditing || !preselectedCollectionId) && (
                collectionOptions.length > 0 ? (
                    <Select
                        label="Collection"
                        value={collectionId}
                        onChange={setCollectionId}
                        options={collectionOptions}
                        error={errors.collectionId}
                    />
                ) : (
                    <div className="text-sm text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                        No collections found for {typeLabel.toLowerCase()}s. Please create a collection first.
                    </div>
                )
            )}

            {/* Initial value fields - only for create mode: Date first, then Value */}
            {!isEditing && (
                <>
                    <Input
                        label="Date"
                        type="date"
                        value={initialDate}
                        onChange={(e) => setInitialDate(e.target.value)}
                        error={errors.initialDate}
                        required
                    />

                    <MoneyInput
                        label="Initial Value"
                        value={initialValue}
                        currency={initialCurrency}
                        onValueChange={setInitialValue}
                        onCurrencyChange={setInitialCurrency}
                        error={errors.initialValue || errors.initialCurrency}
                        placeholder="0.00"
                    />
                </>
            )}

            {/* Tracking mode selector - only for assets (debts always use FINANCIAL) */}
            {type === 'asset' && (
                <RadioGroup
                    label="Tracking Mode"
                    value={trackingMode}
                    onChange={setTrackingMode}
                    options={trackingModeOptions}
                    inline
                />
            )}

            {tagOptions.length > 0 && (
                <SearchableMultiSelect
                    label="Tags"
                    value={tagIds}
                    onChange={setTagIds}
                    options={tagOptions}
                    placeholder="Select tags..."
                />
            )}

            <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                </label>
                <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    placeholder={`Optional description for this ${typeLabel.toLowerCase()}`}
                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-base focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                {errors.description && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {errors.description}
                    </p>
                )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="primary"
                    isLoading={isLoading}
                    disabled={!effectiveCollectionId}
                >
                    {isEditing ? 'Update' : 'Create'} {typeLabel}
                </Button>
            </div>
        </form>
    );
}
